import { Link } from "react-router-dom";
import { motion } from "framer-motion";

function SantharionLogo({ size = 36 }) {
  const s = size, c = s / 2;
  const outerR = s * 0.44, innerR = s * 0.20, pts = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(c + r * Math.cos(a)).toFixed(2)},${(c + r * Math.sin(a)).toFixed(2)}`);
  }
  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} fill="none">
      <defs>
        <linearGradient id="tosLogo" x1="0" y1="0" x2={s} y2={s} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f97316"/><stop offset="50%" stopColor="#ec4899"/><stop offset="100%" stopColor="#8b5cf6"/>
        </linearGradient>
      </defs>
      <polygon points={pts.join(" ")} fill="url(#tosLogo)"/>
    </svg>
  );
}

const Section = ({ title, children }) => (
  <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
    viewport={{ once:true }} className="mb-10">
    <h2 className="text-xl font-bold mb-3" style={{ color:"#f97316" }}>{title}</h2>
    <div className="text-sm leading-relaxed space-y-3" style={{ color:"#9ca3af" }}>{children}</div>
  </motion.div>
);

function TermsOfService() {
  const lastUpdated = "March 2, 2026";

  return (
    <div className="min-h-screen text-white" style={{ background:"#080410", fontFamily:"'Sora',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap');`}</style>

      {/* Nav */}
      <header className="px-6 py-4 flex items-center justify-between"
        style={{ borderBottom:"1px solid rgba(249,115,22,0.12)" }}>
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition">
          <SantharionLogo size={36}/>
          <div>
            <p className="font-black text-sm"
              style={{ background:"linear-gradient(135deg,#f97316,#ec4899,#8b5cf6)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              Santharion AI
            </p>
            <p className="text-xs" style={{ color:"#4b5563" }}>by LearnHubAI</p>
          </div>
        </Link>
        <Link to="/" className="text-xs px-4 py-2 rounded-xl transition hover:text-orange-400"
          style={{ color:"#6b7280", border:"1px solid rgba(255,255,255,0.08)" }}>
          ← Back to Home
        </Link>
      </header>

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-8 text-center">
        <motion.div initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }}>
          <span className="inline-block text-xs font-bold px-3 py-1.5 rounded-full mb-4"
            style={{ background:"rgba(139,92,246,0.1)", color:"#a78bfa", border:"1px solid rgba(139,92,246,0.25)" }}>
            Legal Document
          </span>
          <h1 className="text-4xl md:text-5xl font-black mb-4">
            Terms of{" "}
            <span style={{ background:"linear-gradient(135deg,#8b5cf6,#ec4899)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              Service
            </span>
          </h1>
          <p className="text-sm" style={{ color:"#6b7280" }}>Last updated: {lastUpdated}</p>
        </motion.div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pb-24">
        <div className="rounded-2xl p-8 md:p-10"
          style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(139,92,246,0.12)" }}>

          <Section title="1. Acceptance of Terms">
            <p>
              By accessing or using <strong style={{ color:"#f3f4f6" }}>Santharion AI</strong> (the "Service"),
              provided by <strong style={{ color:"#f3f4f6" }}>LearnHubAI</strong>, you agree to be bound by
              these Terms of Service. If you do not agree to these terms, please do not use the Service.
            </p>
          </Section>

          <Section title="2. Description of Service">
            <p>
              Santharion AI is an AI-powered email automation tool that helps users compose and send
              emails through their own Gmail accounts. The Service uses Google OAuth to authenticate
              users and the Gmail API to send emails on their behalf.
            </p>
            <p>The Service is provided free of charge and is intended for personal and professional email automation.</p>
          </Section>

          <Section title="3. User Eligibility">
            <ul className="list-disc pl-5 space-y-1">
              <li>You must be at least 13 years of age to use this Service</li>
              <li>You must have a valid Google/Gmail account</li>
              <li>You must agree to Google's Terms of Service to use OAuth sign-in</li>
              <li>You are responsible for maintaining the security of your Google account</li>
            </ul>
          </Section>

          <Section title="4. Acceptable Use">
            <p>You agree to use Santharion AI only for lawful purposes. You must NOT use the Service to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Send spam, unsolicited emails, or bulk marketing emails</li>
              <li>Harass, threaten, or harm any individual or organization</li>
              <li>Send emails containing illegal, fraudulent, or harmful content</li>
              <li>Impersonate another person or organization</li>
              <li>Violate any applicable local, national, or international law</li>
              <li>Attempt to circumvent or disrupt the Service's functionality</li>
            </ul>
          </Section>

          <Section title="5. Gmail API Usage">
            <p>
              By signing in with Google, you grant Santharion AI permission to send emails from your
              Gmail account using the <code style={{ color:"#a78bfa", background:"rgba(139,92,246,0.1)", padding:"1px 5px", borderRadius:4 }}>gmail.send</code> scope.
              This permission is used <strong style={{ color:"#f3f4f6" }}>only</strong> when you explicitly
              request the Service to send an email.
            </p>
            <p>You can revoke this access at any time via <a href="https://myaccount.google.com/permissions"
              target="_blank" rel="noreferrer" style={{ color:"#a78bfa", textDecoration:"underline" }}>
              Google Account Permissions</a>.</p>
          </Section>

          <Section title="6. Intellectual Property">
            <p>
              All content, design, and code of Santharion AI and LearnHubAI are owned by LearnHubAI
              and protected by applicable intellectual property laws. You may not copy, reproduce,
              or distribute any part of the Service without written permission.
            </p>
            <p>
              Email content generated by the AI is provided as a suggestion only. You are responsible
              for reviewing and approving all emails before they are sent.
            </p>
          </Section>

          <Section title="7. Disclaimer of Warranties">
            <p>
              The Service is provided <strong style={{ color:"#f3f4f6" }}>"as is"</strong> without warranties
              of any kind, express or implied. LearnHubAI does not warrant that:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The Service will be uninterrupted or error-free</li>
              <li>AI-generated email content will be accurate or appropriate for every situation</li>
              <li>Emails will be delivered successfully (delivery depends on Gmail and recipient servers)</li>
            </ul>
            <p>You use the Service at your own risk and are responsible for reviewing all AI-generated content before sending.</p>
          </Section>

          <Section title="8. Limitation of Liability">
            <p>
              To the fullest extent permitted by law, LearnHubAI shall not be liable for any indirect,
              incidental, special, or consequential damages arising from your use of the Service,
              including but not limited to damages resulting from emails sent through the Service.
            </p>
          </Section>

          <Section title="9. Privacy">
            <p>
              Your use of the Service is also governed by our{" "}
              <Link to="/privacy" style={{ color:"#f97316", textDecoration:"underline" }}>Privacy Policy</Link>,
              which is incorporated into these Terms by reference.
            </p>
          </Section>

          <Section title="10. Modifications to Terms">
            <p>
              LearnHubAI reserves the right to modify these Terms at any time. Changes will be effective
              immediately upon posting. Continued use of the Service after changes constitutes acceptance
              of the new Terms. The "Last updated" date will reflect when changes were made.
            </p>
          </Section>

          <Section title="11. Termination">
            <p>
              We reserve the right to suspend or terminate your access to the Service at any time,
              without notice, if you violate these Terms or engage in any harmful activity through
              the Service.
            </p>
          </Section>

          <Section title="12. Governing Law">
            <p>
              These Terms are governed by the laws of <strong style={{ color:"#f3f4f6" }}>India</strong>.
              Any disputes arising from these Terms shall be subject to the jurisdiction of the courts
              of Karnataka, India.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>If you have questions about these Terms, contact us:</p>
            <div className="rounded-xl p-4 mt-2" style={{ background:"rgba(139,92,246,0.06)", border:"1px solid rgba(139,92,246,0.2)" }}>
              <p><strong style={{ color:"#f3f4f6" }}>Gurucharan P</strong> — Founder, LearnHubAI</p>
              <p>📧 <a href="mailto:cguru203@gmail.com" style={{ color:"#a78bfa" }}>cguru203@gmail.com</a></p>
              <p>📞 <a href="tel:9740043604" style={{ color:"#a78bfa" }}>9740043604</a></p>
            </div>
          </Section>

        </div>
        <p className="text-center text-xs mt-8" style={{ color:"#374151" }}>
          © 2026 Santharion AI · LearnHubAI · All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default TermsOfService;
